import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { loginStart } from "../../store/features/auth/authSlice";
import { Box } from "@mui/system";
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  TextField,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router";

const LoginForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error } = useSelector((state) => state.auth);
  const userLoading = useSelector((state) => state.me?.isLoading);

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onChange",
  });

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPassword = () => {
    navigate("/forgotPassword");
  };

  const onSubmit = (data) => {
    if (!isLoading) {
      const credentials = {
        username: data.email.trim(),
        password: data.password.trim(),
        rememberMe,
      };
      dispatch(loginStart(credentials));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
      <Controller
        name="email"
        control={control}
        rules={{
          required: "L'email è obbligatoria",
          pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          message: "Inserisci un'email valida",
        }}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            variant="filled"
            label="Indirizzo email"
            placeholder="Indirizzo email"
            autoComplete="username"
            type="email"
            sx={{ mb: 2 }}
            error={!!errors.email || !!error}
            helperText={
              errors.email?.message || (error && "Credenziali non valide")
            }
            disabled={isLoading}
          />
        )}
      />
      <Controller
        name="password"
        control={control}
        rules={{
          required: "La password è obbligatoria",
          minLength: {
            value: 6,
            message: "La password deve essere almeno di 6 caratteri",
          },
        }}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            variant="filled"
            label="Password"
            placeholder="Password"
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    edge="end"
                    disabled={isLoading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={!!errors.password}
            helperText={errors.password?.message}
            disabled={isLoading}
          />
        )}
      />
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              color="primary"
              size="small"
              disabled={isLoading}
            />
          }
          label="Ricordami"
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: "10px",
              fontWeight: 400,
            },
          }}
        />
        <Link
          component="button"
          variant="body2"
          sx={{
            fontWeight: 400,
            fontSize: "10px",
            color: "black",
            "&:visited": {
              color: "primary",
            },
          }}
          disabled={isLoading}
          onClick={handleForgotPassword}
        >
          Password dimenticata?
        </Link>
      </Box>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        size="large"
        sx={{ mt: 2, textTransform: "none", height: "50px" }}
        disabled={isLoading}
      >
        {isLoading || userLoading ? "Caricamento..." : "Login"}
      </Button>
    </form>
  );
};

export default LoginForm;
